import type {
  MapShopLayoutDepartmentCss,
  MapShopLayoutDepartmentPosition,
  MapShopLayoutDepartmentVM,
} from '@Model/MapModelsView';
import OwnableTheme from '@Theme';

const DISABLE_MY_SPACE_FEATURE = true;

export const getDepartmentsVM = (
  // @ts-expect-error
  departments: ShopFloorDepartmentDM[]
): MapShopLayoutDepartmentVM[] =>
  getSortedPerPositionDepartments(departments)
    .map((department, index) =>
      getShopLayoutDepartmentVM({
        department,
        position: indexToPositionMap[index],
      })
    )
    .filter((x) => x) // some departments may be missing, so we remove undefined entries
    .filter(
      (x) => !(DISABLE_MY_SPACE_FEATURE && x?.position === 'extra') // MY SPACE feature enabled
    )
    .map((department) => assignCssToDepartmentVM(department!));

const indexToPositionMap: MapShopLayoutDepartmentPosition[] = [
  'center',
  'top',
  'topLeft',
  'left',
  'bottomLeft',
  'bottom',
  'right',
  'topRight',
  'bottomRight',
  'extra',
];

const getSortedPerPositionDepartments = (
  // @ts-expect-error
  departments: ShopFloorDepartmentDM[]
  // @ts-expect-error
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
): (ShopFloorDepartmentDM | undefined)[] => {
  // @ts-expect-error
  const result: ShopFloorDepartmentDM[] = [];

  indexToPositionMap.forEach((_, index) => {
    result.push(departments[index]);
  });

  return result;
};

const getShopLayoutDepartmentVM = ({
  department,
  position,
}: {
  // @ts-expect-error
  department?: ShopFloorDepartmentDM;
  position: MapShopLayoutDepartmentPosition;
}): Omit<MapShopLayoutDepartmentVM, 'ui'> | undefined => {
  if (position === 'extra') {
    return mySpaceDepartment;
  }

  if (!department) {
    return undefined;
  }

  return {
    position,
    title: department.departmentName,
    id: department.departmentId,
  };
};

// https://www.figma.com/file/3PxdzWWVKzf7JdpUqyCqQH/Ownable-Site-Layout?node-id=1560%3A884
const shopFloorMiniMapStyles: Record<
  MapShopLayoutDepartmentPosition,
  MapShopLayoutDepartmentCss
> = {
  topLeft: {
    top: 0,
    left: `${(7.5 / 132) * 100}%`,
    width: `${(36.08 / 132) * 100}%`,
    height: `${(30.41 / 102) * 100}%`,
  },
  left: {
    top: `${((44.05 - 9) / 102) * 100}%`,
    left: 0,
    width: `${(43.58 / 132) * 100}%`,
    height: `${(34.68 / 102) * 100}%`,
  },
  bottomLeft: {
    top: `${((83.37 - 9) / 102) * 100}%`,
    left: `${((27.9 - 12) / 132) * 100}%`,
    width: `${(27.69 / 132) * 100}%`,
    height: `${(18.17 / 102) * 100}%`,
  },
  top: {
    top: `${((23.65 - 9) / 102) * 100}%`,
    left: `${((60.05 - 12) / 132) * 100}%`,
    width: `${(30.37 / 132) * 100}%`,
    height: `${(15.75 / 102) * 100}%`,
  },
  center: {
    top: `${((44.05 - 9) / 102) * 100}%`,
    left: `${((60.05 - 12) / 132) * 100}%`,
    width: `${(43.58 / 132) * 100}%`,
    height: `${(46.55 / 102) * 100}%`,
  },
  bottom: {
    top: `${((95.23 - 9) / 102) * 100}%`,
    left: `${((60.05 - 12) / 132) * 100}%`,
    width: `${(20.54 / 132) * 100}%`,
    height: `${(15.76 / 102) * 100}%`,
  },
  topRight: {
    top: `${((35.71 - 9) / 102) * 100}%`,
    left: `${((107.92 - 12) / 132) * 100}%`,
    width: `${(21.49 / 132) * 100}%`,
    height: `${(15.76 / 102) * 100}%`,
  },
  right: {
    top: `${((56.11 - 9) / 102) * 100}%`,
    left: `${((107.92 - 12) / 132) * 100}%`,
    width: `${(36.08 / 132) * 100}%`,
    height: `${(15.58 / 102) * 100}%`,
  },
  bottomRight: {
    top: `${((75 - 9) / 102) * 100}%`,
    left: `${((107.92 - 12) / 132) * 100}%`,
    width: `${(36.08 / 132) * 100}%`,
    height: `${(15.58 / 102) * 100}%`,
  },
  extra: {
    top: `${((95.23 - 9) / 102) * 100}%`,
    left: `${((107.92 - 12) / 132) * 100}%`,
    width: `${(36.08 / 132) * 100}%`,
    height: `${(15.58 / 102) * 100}%`,
    backgroundColor: OwnableTheme.colors.white,
    borderColor: OwnableTheme.colors.lightGray,
    borderWidth: 1,
  },
};

const assignCssToDepartmentVM = (
  department: Omit<MapShopLayoutDepartmentVM, 'ui'>
): MapShopLayoutDepartmentVM => ({
  ...department,
  ui: shopFloorMiniMapStyles[department.position],
});

const mySpaceDepartment: Omit<MapShopLayoutDepartmentVM, 'ui'> = {
  position: 'extra',
  id: -1,
  title: 'My personal space',
};
